/**
 * Code-based TanStack Router setup. Two zones:
 *  - /sign-in: public
 *  - everything under the `authed` layout: requires a signed-in user
 *    (beforeLoad redirect — the API enforces the real allowlist; this
 *    is just UX).
 * Add app routes as children of `authedRoute`. Use route factories, not
 * hardcoded path strings, when linking (webportal routing standard).
 */
import { createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { AppLayout } from "@/components/AppLayout";
import { auth } from "@/firebase";
import { SignInPage } from "@/modules/auth/components/SignInPage";
import { HomePage } from "@/modules/home/components/HomePage";
import { GalleryPage } from "@/modules/plus/photos/components/GalleryPage";
import { OrphanPhotosPage } from "@/modules/plus/photos/components/OrphanPhotosPage";

const rootRoute = createRootRoute();

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: async () => {
    await auth.authStateReady();
    if (!auth.currentUser) throw redirect({ to: "/sign-in" });
  },
  component: AppLayout,
});

const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: HomePage,
});

const galleryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/gallery",
  component: GalleryPage,
});

const orphanPhotosRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin/orphan-photos",
  component: OrphanPhotosPage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  authedRoute.addChildren([homeRoute, galleryRoute, orphanPhotosRoute]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
